import { classNames, useEventHandler, useUpdateRef } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, {
  Children,
  cloneElement,
  createRef,
  forwardRef,
  useLayoutEffect,
  useRef
} from 'react'
import { focusItem, handleChildCallback } from './menu'

/**
 * @readonly
 * @type {Record<string, import('../common/interfaces').Position>}
 */
const POSITION = {
  LEFT: 'left',
  RIGHT: 'right',
  CENTER: 'center'
}

/**
 * Create a React Nav Menu component
 *
 * @param {import('./interfaces').NavMenuProps} props
 * @param {ref} ref
 * @returns {JSX.Element}
 */
function navMenu (
  {
    children,
    className,
    isOpen,
    onItemKeyDown,
    onItemSelect,
    orientation,
    position,
    withAutoOpen
  },
  ref
) {
  /** @type {import('react').RefObject<HTMLElement>} */
  const elementRef = useRef()
  /** @type {import('react').RefObject<import('react').RefObject<HTMLElement>[]>} */
  const navItemsRef = useRef([])
  const isVertical = orientation !== 'horizontal'

  useUpdateRef(ref, elementRef, { focus })
  useLayoutEffect(positionMenu, [isOpen])
  useEventHandler('resize', positionMenu, document.defaultView)
  useEventHandler('scroll', positionMenu, document.defaultView)

  return (
    <ul
      role={isVertical ? 'menu' : 'menubar'}
      className={classNames(
        {
          'zb-nav-menu-is-open': isOpen
        },
        'zb-nav-menu',
        className
      )}
      ref={elementRef}
    >
      {Children.toArray(children).map(createMenuItem)}
    </ul>
  )

  function focus (index = 0) {
    const allNavItems = navItemsRef.current
    if (index === -1) {
      focusItem(allNavItems[allNavItems.length - 1], -1)
    } else {
      focusItem(allNavItems[index])
    }
  }

  /**
   * @param {import('react').ReactNode} child
   * @param {number} index
   */
  function createMenuItem (child, index) {
    navItemsRef.current[index] = navItemsRef.current[index] || createRef()
    const ref =
      navItemsRef.current[index]
    if (
      child.type &&
      child.type.componentName &&
      child.type.componentName.slice(0, 6) === 'zb.Nav'
    ) {
      return cloneElement(child, {
        isOpen: true,
        ref,
        onItemSelect: handleChildCallback(
          onItemSelect,
          child.props.onItemSelect
        ),
        onItemKeyDown: handleKeyDown.bind(null, index),
        orientation: 'vertical',
        withAutoOpen
      })
    }

    return cloneElement(child, {
      onKeyDown: handleKeyDown.bind(null, index),
      ref
    })
  }

  /**
   * @param {number} groupIndex
   * @param {import('react').KeyboardEvent} event
   * @param {number} childIndex
   */
  function handleKeyDown (groupIndex, event, childIndex) {
    const allMenus = navItemsRef.current
    const menu = allMenus[groupIndex]
    const menuItems = menu.current && menu.current.navItemsRef.current
    let newFocusedGroup = groupIndex
    let newFocusedGroupChild = childIndex

    switch (event.key) {
      case 'ArrowRight':
        if (groupIndex + 1 < allMenus.length) {
          newFocusedGroup = groupIndex + 1
        }
        newFocusedGroupChild = 0
        break
      case 'ArrowLeft':
        if (groupIndex > 0) {
          newFocusedGroup = groupIndex - 1
        }
        newFocusedGroupChild = 0
        break
      case 'ArrowDown':
        if (
          childIndex != null &&
          menuItems.length &&
          childIndex >= menuItems.length
        ) {
          newFocusedGroup =
            groupIndex + 1 < allMenus.length ? groupIndex + 1 : 0
          newFocusedGroupChild = 0
        }
        break
      case 'ArrowUp':
        if (childIndex === -1) {
          if (groupIndex > 0) {
            newFocusedGroup = groupIndex - 1
          } else {
            newFocusedGroup = allMenus.length - 1
          }
          newFocusedGroupChild =
            allMenus[newFocusedGroup].current.navItemsRef.current.length - 1
        }
        break
      default:
        return onItemKeyDown(event, newFocusedGroup, newFocusedGroupChild)
    }
    focusItem(allMenus[newFocusedGroup], newFocusedGroupChild)
    return onItemKeyDown(event, groupIndex, childIndex)
  }

  function positionMenu () {
    if (isOpen) {
      const menu = elementRef.current
      const menuParent = menu.parentNode
      const viewportWidth = document.documentElement.clientWidth
      const menuBounds = menu.getBoundingClientRect()
      const parentBounds = menuParent.getBoundingClientRect()
      let left

      if (position === POSITION.CENTER) {
        left = parentBounds.left - menuBounds.width / 2 + parentBounds.width / 2
      } else if (position === POSITION.LEFT) {
        left = parentBounds.left
      } else {
        left = parentBounds.left + parentBounds.width - menuBounds.width
      }

      menu.style.top = parentBounds.bottom + 'px'
      menu.style.left =
        Math.min(Math.max(left, 0), viewportWidth - menuBounds.width) + 'px'
    }
  }
}

const NavMenu = forwardRef(navMenu)

NavMenu.propTypes = {
  position: PropTypes.oneOf(Object.values(POSITION)),
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  onItemKeyDown: PropTypes.func,
  onItemSelect: PropTypes.func,
  withAutoOpen: PropTypes.bool
}

NavMenu.defaultProps = {
  position: POSITION.CENTER,
  isOpen: false
}

NavMenu.displayName = 'NavMenu'
NavMenu.componentName = 'zb.NavMenu'

export default NavMenu
